import jsPDF from "jspdf";
import {
  captureDocumentCanvas,
  sliceCanvasToPages,
  PAGE_WIDTH_PT,
  PAGE_HEIGHT_PT,
  MARGIN_X_PT,
  HEADER_PT,
  CONTENT_HEIGHT_PT,
} from "./pdfCapture";
import { loadImageAsDataUrl } from "./pdfHelpers";

const LOGO_MAX_H_PT = 20;
const LOGO_MAX_W_PT = 90;

// Filesystem-safe form of a value going into a filename.
const slug = (value) =>
  String(value || "")
    .replace(/[/\\:*?"<>|]/g, "")
    .trim()
    .replace(/\s+/g, "-");

// "PDF filename format" from report settings — {client}, {quarter} and
// {provider} are substituted, anything else is left alone. Falls back to the
// previous hard-coded shape when a report carries no format (an older payload,
// or a settings row that predates the field).
export function buildPdfFilename(draft) {
  const client = slug(draft?.clientName) || "report";
  const quarter = slug(draft?.periodLabel);
  const provider = slug(draft?.providerName);

  const format = typeof draft?.filenameFormat === "string" ? draft.filenameFormat.trim() : "";
  if (!format) return `Quarterly-PCR-${client}-${quarter.replace(/[^\w-]+/g, "-")}.pdf`;

  const filled = format
    .replace(/\{client\}/gi, client)
    .replace(/\{quarter\}/gi, quarter)
    .replace(/\{provider\}/gi, provider)
    // A placeholder that resolved to nothing can leave a dangling separator.
    .replace(/[_-]{2,}/g, "_")
    .replace(/[_-]+(?=\.pdf$)/i, "")
    .trim();

  const safe = slug(filled) || `Quarterly-PCR-${client}`;
  return /\.pdf$/i.test(safe) ? safe : `${safe}.pdf`;
}

// Real PDF export for a Quarterly PCR draft. Rather than redrawing the layout
// a second time with jsPDF's own text/line primitives (which could only ever
// approximate the on-screen preview), this rasterizes the exact same
// PdfDocument component captureDocumentCanvas also captures for the preview —
// the download is a literal capture of what was previewed, guaranteed
// pixel-identical, sliced across as many physical pages as the content needs.
export async function generateReportPdf(draft) {
  if (!draft) return;

  const canvas = await captureDocumentCanvas(draft);
  const pages = sliceCanvasToPages(canvas, CONTENT_HEIGHT_PT);

  // "Include provider logo on PDF" — the server only sets providerLogo when
  // that setting is on, so its presence is the whole decision here. Resolved
  // to bytes once, before the page loop: jsPDF's addImage cannot take a URL,
  // and the same logo is drawn on every page.
  const logo = await loadLogo(draft.providerLogo);

  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const runHead = `${draft.title || "Quarterly Person-Centered Review"} · ${draft.clientName || ""} · ${draft.periodLabel || ""}`;

  pages.forEach((page, i) => {
    if (i > 0) doc.addPage();

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor("#9a9a9a");
    // The run head is truncated to the space the logo leaves rather than
    // being allowed to run under it.
    const headRoom = PAGE_WIDTH_PT - MARGIN_X_PT * 2 - (logo ? logo.width + 12 : 0);
    doc.text(runHead, MARGIN_X_PT, 24, { maxWidth: Math.max(60, headRoom) });
    if (logo) {
      doc.addImage(
        logo.dataUrl,
        logo.format,
        PAGE_WIDTH_PT - MARGIN_X_PT - logo.width,
        24 - logo.height + 2,
        logo.width,
        logo.height
      );
    }
    doc.setDrawColor("#e5e5e5");
    doc.line(MARGIN_X_PT, 30, PAGE_WIDTH_PT - MARGIN_X_PT, 30);

    doc.addImage(page.dataUrl, "PNG", 0, HEADER_PT, page.widthPt, page.heightPt);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor("#aaaaaa");
    doc.text(`Page ${i + 1} of ${pages.length}`, PAGE_WIDTH_PT / 2, PAGE_HEIGHT_PT - 18, { align: "center" });
  });

  doc.save(buildPdfFilename(draft));
}

// Data URL plus the drawn size, scaled to fit the header band while keeping
// the logo's own aspect ratio. Returns null for anything that can't be fetched
// or measured — a header without a logo still prints.
async function loadLogo(url) {
  const dataUrl = await loadImageAsDataUrl(url);
  if (!dataUrl) return null;

  const format = /^data:image\/(png|jpe?g)/i.exec(dataUrl)?.[1]?.toUpperCase();
  // jsPDF only takes raster formats it can embed; an SVG logo would throw.
  if (!format) return null;

  try {
    const { width, height } = await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () => reject(new Error("Logo failed to decode"));
      img.src = dataUrl;
    });
    if (!width || !height) return null;
    const scale = Math.min(LOGO_MAX_W_PT / width, LOGO_MAX_H_PT / height, 1);
    return {
      dataUrl,
      format: format === "JPG" ? "JPEG" : format,
      width: width * scale,
      height: height * scale,
    };
  } catch {
    return null;
  }
}

export default generateReportPdf;
